import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import '../reports/EIRReport.css';
import { openFilePreview } from '../common/FilePreviewModal';
const tpGetHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('tp_token')}`,
    'Content-Type': 'application/json',
});

const fmtDate = value => value ? new Date(String(value).length === 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-GB') : '—';

const todayInput = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const calculateDemurrage = (dropOffDate, returnDate) => {
    if (!dropOffDate) return null;
    const start = new Date(`${dropOffDate}T00:00:00`);
    const end = new Date(`${returnDate || todayInput()}T00:00:00`);
    return Math.round((end - start) / 86400000);
};

const TransporterEIR = () => {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});
    const [expandedIdf, setExpandedIdf] = useState({});
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const fetchRows = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const query = new URLSearchParams({ search: appliedSearch });
            const res = await fetch(`/api/transporter/eir-report/?${query}`, { headers: tpGetHeaders() });
            if (!res.ok) throw new Error('Failed to load EIR report.');
            const data = await res.json();
            setRows(data.results || []);
        } catch (error) {
            toast.error(error.message || 'Failed to load EIR report.');
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => { fetchRows(); }, [appliedSearch]);

    const displayRows = useMemo(() => {
        if (!appliedSearch.trim()) return rows;
        const term = appliedSearch.trim().toLowerCase();
        return rows
            .map(row => ({
                ...row,
                containers: (row.containers || []).filter(c => (c.container_no || '').toLowerCase().includes(term))
            }))
            .filter(row =>
                (row.idf_no || '').toLowerCase().includes(term) ||
                (row.bl_no || '').toLowerCase().includes(term) ||
                (row.containers || []).length > 0
            );
    }, [rows, appliedSearch]);

    const releaseDateGroups = useMemo(() => Object.values(displayRows.reduce((groups, row) => {
        const key = row.report_date || 'unknown';
        if (!groups[key]) groups[key] = { key, date: row.report_date, rows: [] };
        groups[key].rows.push(row);
        return groups;
    }, {})), [displayRows]);

    const totalPendingContainers = useMemo(() =>
        releaseDateGroups.reduce((total, group) =>
            total + group.rows.reduce((rowTotal, row) => rowTotal + (row.containers || []).length, 0)
        , 0),
    [releaseDateGroups]);

    useEffect(() => {
        if (!appliedSearch.trim()) return;
        const next = {};
        releaseDateGroups.forEach(group => {
            next[group.key] = true;
            group.rows.forEach(row => {
                if ((row.containers || []).length > 0) {
                    next[`idf-${row.id}`] = true;
                }
            });
        });
        setExpandedGroups(next);
    }, [appliedSearch, releaseDateGroups]);

    const renderContainerTable = row => (
        <div style={{ overflowX: 'auto' }}>
            <table className="jobs-table" style={{ minWidth: 600, borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {['CONTAINER #', 'RELEASED ON', 'DROP-OFF DATE', 'DROP-OFF LOC'].map(header => (
                            <th key={header} style={{ background: '#fff4cc', fontWeight: 700, border: '1px solid #b9c9d8', padding: 6 }}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {(row.containers || []).map(container => (
                        <tr key={container.id}>
                            <td style={{ border: '1px solid #d4dce3', padding: 6 }}>
                                {container.container_no}
                                <br/>
                                {container.size ? `(${container.size}${container.container_type ? ' ' + container.container_type : ''})`.trim() : ''}
                            </td>
                            <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{fmtDate(container.release_date)}</td>
                            <td style={{ border: '1px solid #d4dce3', padding: 6 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                    {fmtDate(container.drop_off_expiry_date)}
                                    {(container.drop_off_attachment_url || row.drop_off_attachment_url) && <button type="button" className="dialog-btn dialog-btn-primary dialog-btn-sm" onClick={() => openFilePreview(container.drop_off_attachment_url || row.drop_off_attachment_url, 'Dropoff Attachment')}>View</button>}
                                </div>
                            </td>
                            <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{container.drop_off_location || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    const renderIdfTable = idfRows => {
        const showClientRef = idfRows.some(r => r.customer_ref);
        const colSpan = showClientRef ? 7 : 6;
        return (
            <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                    <tr>
                        {['IDF', 'CUSTOMER', ...(showClientRef ? ['CLIENT REF #'] : []), 'BL #', 'ICD', '# CONTAINERS', 'DETAILS'].map(header => (
                            <th key={header} style={{ background: '#82acfa', fontWeight: 700, border: '1px solid #b9c9d8', padding: 6 }}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {idfRows.map((row, idx) => {
                        const detailOpen = expandedIdf[`idf-${row.id}`];
                        return (
                            <React.Fragment key={row.id}>
                                <tr style={{ backgroundColor: idx % 2 === 0 ? '#e3f2fd' : '#fff' }}>
                                    <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{row.idf_no || '—'}</td>
                                    <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{row.importer || '—'}</td>
                                    {showClientRef && (
                                        <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{row.customer_ref || '—'}</td>
                                    )}
                                    <td style={{ border: '1px solid #d4dce3', padding: 6 }}>
                                        {row.bl_attachment_url ? (
                                            <a style={{ color: '#005f9e', textDecoration: 'underline', cursor: 'pointer' }} onClick={() => openFilePreview(row.bl_attachment_url, 'Bill of Lading')}>
                                                {row.bl_no || 'View BL'}
                                            </a>
                                        ) : (
                                            row.bl_no || '—'
                                        )}
                                    </td>
                                    <td style={{ border: '1px solid #d4dce3', padding: 6 }}>{row.icd || '—'}</td>
                                    <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>{(row.containers || []).length}</td>
                                    <td style={{ border: '1px solid #d4dce3', padding: 6 }}>
                                        <button
                                            type="button"
                                            className="icon-btn expand-btn"
                                            onClick={() => setExpandedIdf(current => ({ ...current, [`idf-${row.id}`]: !detailOpen }))}
                                            title={detailOpen ? 'Collapse Details' : 'Expand Details'}
                                        >
                                            <i className={`fa ${detailOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                        </button>
                                    </td>
                                </tr>
                                {detailOpen && (
                                    <>
                                        <tr>
                                            <td colSpan={colSpan} style={{ padding: '8px 0 8px 16px', border: 'none' }}>
                                                <div style={{ borderLeft: '4px solid #f0ad4e', paddingLeft: 12 }}>{renderContainerTable(row)}</div>
                                            </td>
                                        </tr>
                                        <tr><td colSpan={colSpan} style={{ height: 8, border: 'none', backgroundColor: '#cecdcd' }} /></tr>
                                    </>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    return (
        <div className="report-container eir-report" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 0 }}>
                <h2 style={{ margin: 0 }}>EIR - Pending</h2>
                <button type="button" className="dialog-btn dialog-btn-primary" onClick={() => fetchRows()} disabled={loading}>
                    <i className="fa fa-refresh"></i> Refresh
                </button>
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '16px 0' }}>
                <input
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') setAppliedSearch(search.trim()); }}
                    placeholder="Search by IDF, BL or Container..."
                    style={{ fontSize: 12, padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', width: 240, height: 28, boxSizing: 'border-box' }}
                />
                <button type="button" className="dialog-btn dialog-btn-primary" onClick={() => setAppliedSearch(search.trim())}>
                    <i className="fa fa-search"></i> Search
                </button>
                <button
                    type="button"
                    className="dialog-btn dialog-btn-secondary"
                    onClick={() => { setSearch(''); setAppliedSearch(''); }}
                >
                    <i className="fa fa-times"></i> Clear
                </button>
            </div>

            {loading ? <div className="loading">Loading...</div> : releaseDateGroups.length === 0 ? (
                <div className="no-data">No records found.</div>
            ) : (
                <table className="jobs-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            {['DATE', 'DAYS SINCE RELEASED', `# CONTAINERS INTERCHANGE PENDING - ${totalPendingContainers}`, 'DETAILS'].map(header => (
                                <th key={header} style={{ background: '#e5def1', fontWeight: 700, fontSize: '1em', border: '1px solid #b9c9d8', padding: 6 }}>
                                    <div>{header}</div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {releaseDateGroups.map((group, idx) => {
                            const groupKey = group.key;
                            const groupOpen = expandedGroups[groupKey];
                            const daysSinceReleased = calculateDemurrage(group.date, '');
                            const pendingContainers = group.rows.reduce((total, row) => total + (row.containers || []).length, 0);
                            const groupTransporters = Array.from(new Set(
                                group.rows
                                    .flatMap(row => row.containers || [])
                                    .map(container => container.transporter)
                                    .filter(t => t && t !== '—')
                            )).sort().join(' | ') || '—';
                            return (
                                <React.Fragment key={groupKey}>
                                    <tr onClick={() => setExpandedGroups(current => ({ ...current, [groupKey]: !groupOpen }))} style={{ backgroundColor: idx % 2 === 0 ? '#f3e5f5' : '#fff', cursor: 'pointer' }}>
                                        <td style={{ border: '1px solid #d4dce3', padding: 6, fontWeight: 700, fontSize: '1.15em' }}>{fmtDate(group.date)}</td>
                                        <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center', fontSize: '1.15em' }} className={daysSinceReleased !== null && daysSinceReleased >= 5 ? 'eir-release-status-alert-red' : daysSinceReleased !== null && daysSinceReleased >= 3 ? 'eir-release-status-alert-orange' : 'eir-release-status'}>
                                            {daysSinceReleased ?? 0} {daysSinceReleased > 1 ? 'DAYS' : 'DAY'}
                                        </td>
                                        <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center', fontSize: '1.15em' }} className="eir-release-status">
                                            {pendingContainers} {pendingContainers === 1 ? 'CONTAINER' : 'CONTAINERS'}
                                        </td>
                                        <td style={{ border: '1px solid #d4dce3', padding: 6, textAlign: 'center' }}>
                                            <button type="button" className="icon-btn expand-btn" onClick={event => { event.stopPropagation(); setExpandedGroups(current => ({ ...current, [groupKey]: !groupOpen })); }} title={groupOpen ? 'Collapse Details' : 'Expand Details'}>
                                                <i className={`fa ${groupOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                                            </button>
                                        </td>
                                    </tr>
                                    {groupOpen && (
                                        <>
                                            <tr>
                                                <td colSpan={5} style={{ padding: '8px 0 8px 16px', border: 'none' }}>
                                                    <div style={{ borderLeft: '4px solid #8e24aa', paddingLeft: 12 }}>{renderIdfTable(group.rows)}</div>
                                                </td>
                                            </tr>
                                            <tr><td colSpan={5} style={{ height: 8, border: 'none', backgroundColor: '#cecdcd' }} /></tr>
                                        </>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default TransporterEIR;
